import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Box,
  Modal,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import ComboBox from "../ComboBox";
import AccessibleTooltip from "../Tooltip/Tooltip.js";
import closeIcon from "../../assets/images/closeicon.svg";
import { fieldUser } from "./FieldJson";
import attachmentIcon from "../../assets/images/attachmentIcon.png";
import { useDispatch, useSelector } from "react-redux";
import checkImg from "../../assets/images/check_circle.svg";

import {
  addFloor,
  getUserListAction,
  editFloor,
  fetchBuildingDataAction,
} from "../../Actions/FloorManagementAction.js";
import PropTypes from "prop-types";
import '../../assets/styles/form.scss';
import "./style.scss";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  paddingLeft: 4,
  paddingRight: 4,
}; 

const AddEditFloor = (props) => {
  const [formData, setFormData] = useState({
    buildingId: "",
    customerName: "",
    floor: "",
    uploadedFile: null,
  });
  const [formError, setFormError] = useState({});
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const { setOpen, open, actionButton, setActionButton, editData } = props;
  const firstInputRef = useRef(null);
  const {
    userList,
    editSuccess,
    editError,
    addSuccess,
    addError,
    error,
    buildingData,
    buildingDataLoading,
  } = useSelector((state) => state.FloorManagementReducer);

  const dispatch = useDispatch();

  // input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formError[name]) {
      setFormError((prev) => ({ ...prev, [name]: null }));
    }
  };

  // building select via ComboBox
  const handleBuildingChange = (value) => {
    const selected = buildingData.find((b) => `${b.id}` === value);
    let customer = "";
    if (selected?.customer) {
      customer = `${selected.customer.firstName.trim()} ${selected.customer.lastName.trim()} - ${selected.customer.customerId}`;
    }
    setFormData((prev) => ({
      ...prev,
      buildingId: selected?.id.toString() || "",
      customerName: customer,
    }));
  };

  // fetch user & building list
  useEffect(() => {
    if (open) {
      dispatch(getUserListAction());
      dispatch(fetchBuildingDataAction());
      setFormError({});
      dispatch({ type: "CLEAR_FLOOR_ERRORS" });
    }
  }, [open, dispatch]);

  // validation
  const handleValidation = () => {
    let isValid = true;
    let errors = {};
    const required =
      actionButton === "edit"
        ? ["buildingId", "floor"]
        : ["buildingId", "floor", "uploadedFile"];

    required.forEach((field) => {
      if (!formData[field]) {
        isValid = false;
        errors[field] =
          field === "floor"
            ? "Please enter floor name"
            : field === "buildingId"
            ? "Please select building"
            : "Please upload floor plan file";
      }
    });

    if (formData.floor && /[^a-zA-Z0-9\s]/.test(formData.floor)) {
      isValid = false;
      errors.floor = "Special characters are not allowed in the floor field.";
    }

    setFormError(errors);
    return isValid;
  };

  // error handling
  useEffect(() => {
    if (error) {
      setFormError((prev) => ({ ...prev, floor: error }));
      setLoading(false);
    }
  }, [error]);

  // file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    const allowed = ["image/jpeg", "image/png"];
    const maxSize = 2 * 1024 * 1024;

    if (file) {
      if (!allowed.includes(file.type)) {
        setFormError((prev) => ({
          ...prev,
          uploadedFile: "Only .jpg and .png files are allowed.",
        }));
        setUploadedFile(null);
        setFormData((prev) => ({ ...prev, uploadedFile: null }));
        return;
      }

      if (file.size > maxSize) {
        setFormError((prev) => ({
          ...prev,
          uploadedFile: "File size must not exceed 2 MB.",
        }));
        setUploadedFile(null);
        setFormData((prev) => ({ ...prev, uploadedFile: null }));
        return;
      }

      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        if (img.width <= img.height) {
          setFormError((prev) => ({
            ...prev,
            uploadedFile:
              "Image format is not correct. Width must be greater than height.",
          }));
          setUploadedFile(null);
          setFormData((prev) => ({ ...prev, uploadedFile: null }));
        } else {
          setUploadedFile(file);
          setFormData((prev) => ({ ...prev, uploadedFile: file }));
          setFormError((prev) => ({ ...prev, uploadedFile: null }));
        }
      };
    }
  };

  // add/edit
  const handleAdd = (e) => {
    e.preventDefault();
    setLoading(true);
    dispatch(addFloor(formData, uploadedFile));
  };
  const handleEdit = () => {
    setLoading(true);
    dispatch(editFloor(formData, uploadedFile, editData.id));
  };

  // success/error cleanup
  useEffect(() => {
    if (editSuccess || addSuccess) {
      // dispatch({ type: "RESET_ADD_EDIT_FLOOR" });
      // setOpen(false);
      // setActionButton("");
      // setFormError({});
      // setFormData({ buildingId: "", customerName: "", floor: "", uploadedFile: null });
      // setUploadedFile(null);
      setLoading(false);
    } else if (editError || addError) {
      dispatch({ type: "RESET_ADD_EDIT_FLOOR" });
      if (editError === "Floor with same name already exists") {
        setFormError((prev) => ({ ...prev, floor: editError }));
      }
      setLoading(false);
    }
  }, [editSuccess, editError, addError, addSuccess, dispatch, setOpen, setActionButton]);

  // submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!handleValidation()) return;
    actionButton === "add" ? handleAdd(e) : handleEdit();
  };

  const handleClose = () => {
    setOpen(false);
    setActionButton("");
    setFormError({});
    setFormData({ buildingId: "", customerName: "", floor: "", uploadedFile: null });
    setUploadedFile(null);
  };

  // preload edit data
  useEffect(() => {
    if (actionButton === "edit" && editData) {
      const floorPlan = editData.floorPlan || "";
      const uploadedFileName = floorPlan.split("/").pop();
      setFormData({
        buildingId: editData.buildingId.toString() || "",
        customerName: editData.customerNameId || "",
        floor: editData.floor || "",
        uploadedFileName,
        floorPlan,
      });
    } else {
      setFormData({ buildingId: "", customerName: "", floor: "", uploadedFile: null });
    }
  }, [actionButton, editData]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
          if (open && firstInputRef.current) {
            firstInputRef.current.focus();
          }
        }, 200);
        return () => {
          clearTimeout(timeoutId);
        };
      }, [open]);
  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason === "escapeKeyDown") {
          if (tooltipOpen) {
            setTooltipOpen(false);
          } else {
            handleClose();
          }
        } else if (reason === "backdropClick") {
          return;
        }
      }}
      className="globalForm addFloorEntry"
      >
      <div role="none">
      <Box
        role="dialog"
        aria-modal="true"
        sx={style}
        aria-labelledby="add-edit-floor-title"
        className="mainModalBox"
      >
        <span aria-live="polite" className="sr-only" data-testId="errors">
            {Object.values(formError).filter(Boolean).join(", ")}
          </span>
        <div
          className="Modal_header"
        >
            <h1 id="add-edit-floor-title" data-testId="add-edit-floor-title">
              {actionButton === "edit"
                ? "Edit Floor"
                : "Create New Floor"}
            </h1>
          <AccessibleTooltip title="Close" open={tooltipOpen} customclass={"modalcloseTooltip"}>
            <button
              ref={firstInputRef}
              onClick={() => handleClose()}
              className={"close_button_modal"}
              aria-label="Close"
              data-testid="close-button"
              onFocus={() => setTooltipOpen(true)}
              onBlur={() => setTooltipOpen(false)}
              onMouseEnter={() => setTooltipOpen(true)}
              onMouseLeave={() => setTooltipOpen(false)}
            >
              <img src={closeIcon} alt="" aria-hidden="true" />
            </button>
          </AccessibleTooltip>
        </div>
        <div className="Modal_middle">
       <Box component="form" noValidate autoComplete="off" className="field_addDevice">
  {!editSuccess && !addSuccess ? (
    fieldUser.map((data, index) => {

      // ============ INPUT FIELD ============ 
      if (data.type === "input") {
        return (
          <TextField
            key={data.id || data.name || index}
            id={data.id}
            slotProps={{
              "aria-describedby": `${data.id}-helper-text`,
              "data-testid": data.id,
            }}
            name={data.name}
            label={data.label}
            variant="outlined"
            disabled={data.disabled}
            onChange={handleInputChange}
            value={formData[data.name] || ""}
            error={!!formError[data.name]}
            className="innerFields"
            helperText={formError[data.name] || data.helperText}
            fullWidth
            sx={{
              "& .MuiInputLabel-root": { transform: "inherit" },
            }}
          />
        );
      }

      // ============ SELECT FIELD ============
      if (data.type === "select") {
        return (
          <FormControl key={data.id || index} className="innerFields" fullWidth>
            <InputLabel id="building-label">{data.label}</InputLabel>
            <ComboBox
              label="Select Building"
              options={buildingData.map((b) => `${b.id}`)}
              value={formData.buildingId}
              onChange={handleBuildingChange}
            />
            <FormHelperText error={!!formError.buildingId}>
              {formError.buildingId}
            </FormHelperText>
          </FormControl>
        );
      }

      // ============ FILE UPLOAD FIELD ============
      return (
        <TextField
          key={index}
          label={data.label}
          value={
            (actionButton === "add" && uploadedFile?.name) ||
            (actionButton === "edit" && formData.uploadedFileName) ||
            ""
          }
          helperText={formError.uploadedFile}
          className="innerFields attachmentField"
          InputProps={{
            readOnly: true,
            endAdornment: (
              <Button
                variant="contained"
                component="label"
                className="attachmentBtn"
                aria-label="upload floor plan"
              >
                <img src={attachmentIcon} alt="" />
                <input type="file" hidden accept="image/*" onChange={handleFileUpload} />
              </Button>
            ),
            "data-testid": "file-upload-input",
          }}
          error={!!formError.uploadedFile}
        />
      );
    })
  ) : (
    <div className="emailsent-wrapper manageUser_details">
      <img src={checkImg} alt="" aria-hidden="true" />
      <h2>Success</h2>
      <p>Floor has {addSuccess ? "created" : "updated"} successfully.</p>
    </div>
  )}
</Box>


        </div>
        {!(editSuccess || addSuccess) && (<div className="Modal_footer adddeviceFooter">
          <Box sx={{ display: "flex" }}>
            <Button variant="outlined" className="cancelBtn" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="contained" className="saveBtn" onClick={handleSubmit}>
              Save
            </Button>
            {loading && (
              <div className="loader-overlay">
                <CircularProgress size={50} />
              </div>
            )}
          </Box>
        </div>)}
      </Box>
      </div>
    </Modal>
  );
};

AddEditFloor.propTypes = {
  setOpen: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  actionButton: PropTypes.string.isRequired,
  setActionButton: PropTypes.func.isRequired,
  editData: PropTypes.object,
};

export default AddEditFloor;
